<template>
  <dialog ref="dialog" class="contact-dialog" aria-labelledby="contact-title"
    data-lenis-prevent @cancel="cancel" @close="onClose" @keydown.tab="trapFocus">
    <div class="contact-content">
      <button type="button" class="close" aria-label="Close contact form"
        :disabled="sending" @click="close">×</button>
      <p class="eyebrow">Hobson Bernardino + Davis LLP</p>
      <template v-if="sent">
        <h2 id="contact-title" ref="heading" tabindex="-1">Thank you.</h2>
        <p>Your inquiry has been submitted.</p>
        <p>If your matter aligns with our practice focus, we’ll contact you to
          schedule a confidential discussion.</p>
        <button type="button" class="primary" @click="close">Done</button>
      </template>
      <template v-else>
        <p class="progress">Step {{ step }} of 2 · {{ step === 1 ? 'Before we begin' : 'Your inquiry' }}</p>
        <h2 id="contact-title" ref="heading" tabindex="-1">Message us.</h2>
        <form @submit.prevent="submit" :aria-busy="sending">
          <template v-if="step === 1">
            <p id="contact-notice">Submitting this form does not establish an attorney-client
              relationship, and the information provided is not confidential until
              authorized in writing.</p>
            <a class="text-link" href="/disclaimer" target="_blank" rel="noopener">
              Full disclaimer <span class="sr-only">(opens in a new tab)</span> ↗
            </a>
            <label class="consent">
              <input v-model="answers.consent" name="consent" type="checkbox" required
                aria-describedby="contact-notice" />
              <span>I accept the terms and conditions.</span>
            </label>
          </template>
          <fieldset v-else :disabled="sending">
            <legend class="sr-only">Your contact details and inquiry</legend>
            <p class="help">All fields are required. Please do not include confidential or sensitive information.</p>
            <label for="contact-name">Full name</label>
            <input id="contact-name" v-model="answers.name" name="name" autocomplete="name"
              required maxlength="200" pattern=".*\S.*" />
            <label for="contact-email">Email address</label>
            <input id="contact-email" v-model="answers.email" name="email" type="email"
              autocomplete="email" required maxlength="254" />
            <label for="contact-message">Case details / inquiry</label>
            <textarea id="contact-message" v-model="answers.message" name="message" rows="5"
              required minlength="5" maxlength="500" aria-describedby="contact-limit" />
            <p id="contact-limit" class="help character-count">{{ answers.message.length }} / 500 characters · Minimum 5</p>
            <ContactCaptcha v-if="isOpen" :key="captchaAttempt" @token="captchaToken = $event" />
          </fieldset>
          <div hidden aria-hidden="true">
            <label>Fax<input v-model="answers.fax" name="fax" tabindex="-1" autocomplete="off" /></label>
          </div>
          <p v-if="error" ref="errorMessage" role="alert" tabindex="-1" class="error">{{ error }}</p>
          <div class="actions">
            <button v-if="step === 2" type="button" class="secondary" :disabled="sending" @click="back">Back</button>
            <button type="submit" class="primary" :disabled="sending || (step === 2 && !captchaToken)">
              {{ step === 1 ? 'Continue →' : sending ? 'Sending…' : 'Send inquiry →' }}
            </button>
          </div>
        </form>
      </template>
    </div>
  </dialog>
</template>

<script setup>
import { nextTick, onBeforeUnmount, reactive, ref } from "vue";
import ContactCaptcha from "./ContactCaptcha.vue";

const dialog = ref(null);
const heading = ref(null);
const errorMessage = ref(null);
const isOpen = ref(false);
const step = ref(1);
const sent = ref(false);
const sending = ref(false);
const error = ref("");
const captchaToken = ref("");
const captchaAttempt = ref(0);
const emptyAnswers = () => ({ consent: false, name: "", email: "", message: "", fax: "" });
const answers = reactive(emptyAnswers());
let controller;
let previousOverflow;
let trigger;

async function focusHeading() {
  await nextTick();
  heading.value?.focus();
  dialog.value.scrollTop = 0;
}
function open() {
  if (isOpen.value) return;
  if (sent.value) {
    sent.value = false;
    step.value = 1;
    Object.assign(answers, emptyAnswers());
  }
  trigger = document.activeElement;
  previousOverflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";
  isOpen.value = true;
  dialog.value.showModal();
  focusHeading();
}
function close() { if (!sending.value) dialog.value.close(); }
function cancel(event) { if (sending.value) event.preventDefault(); }
function trapFocus(event) {
  const controls = [...dialog.value.querySelectorAll(
    'button, input, textarea, a[href], iframe, [tabindex="0"]'
  )].filter(element => !element.matches(":disabled") && element.tabIndex >= 0 && element.getClientRects().length);
  const first = controls[0];
  const last = controls[controls.length - 1];
  if (!first) {
    event.preventDefault();
    heading.value?.focus();
  } else if (event.shiftKey && (document.activeElement === first || !controls.includes(document.activeElement))) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}
function onClose() {
  isOpen.value = false;
  captchaToken.value = "";
  document.body.style.overflow = previousOverflow;
  trigger?.focus({ preventScroll: true });
}
function back() {
  step.value = 1;
  error.value = "";
  captchaToken.value = "";
  focusHeading();
}
async function submit() {
  if (sending.value) return;
  error.value = "";
  if (step.value === 1) {
    if (!answers.consent) return;
    step.value = 2;
    focusHeading();
    return;
  }
  if (!captchaToken.value) return;
  sending.value = true;
  controller = new AbortController();
  const timeout = window.setTimeout(() => controller?.abort(), 25000);
  try {
    const response = await fetch("/api/contact", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ ...answers, captchaToken: captchaToken.value }),
      signal: controller.signal,
    });
    const result = await response.json();
    if (!response.ok) {
      const fields = result?.fields;
      error.value = fields && typeof fields === "object"
        ? Object.values(fields).filter(value => typeof value === "string").join(" ") : "";
      error.value ||= typeof result?.error === "string" ? result.error : "Your inquiry could not be sent. Please try again later.";
      if (fields?.consent) step.value = 1;
    } else {
      if (response.status !== 202 || result?.status !== "accepted") throw new Error("Not accepted");
      sent.value = true;
      Object.assign(answers, emptyAnswers());
      focusHeading();
    }
  } catch {
    error.value = "We could not confirm your submission. Your answers are still here. Please try again later.";
  } finally {
    window.clearTimeout(timeout);
    controller = null;
    sending.value = false;
    captchaToken.value = "";
    captchaAttempt.value += 1;
    if (error.value) {
      await nextTick();
      errorMessage.value?.focus();
    }
  }
}
onBeforeUnmount(() => {
  controller?.abort();
  if (isOpen.value) document.body.style.overflow = previousOverflow;
});
defineExpose({ open });
</script>

<style scoped>
.contact-dialog {
  box-sizing: border-box;
  width: min(640px, calc(100% - 24px));
  max-height: calc(100dvh - 32px);
  margin: auto;
  padding: 0;
  border: 1px solid var(--color-brand_line);
  border-radius: 12px;
  background: var(--color-bg_base);
  color: var(--color-text_primary);
  box-shadow: 0 24px 80px #0009;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.6;
  text-align: left;
  overscroll-behavior: contain;
  color-scheme: dark;
}
.contact-dialog::backdrop { background: #000b; backdrop-filter: blur(4px); }
.contact-content { padding: 40px; position: relative; }
.close { position: absolute; top: 8px; right: 8px; width: 44px; height: 44px; font-size: 28px; }
.eyebrow { color: var(--color-brand_light); font-size: 0.75em; letter-spacing: 0.08em; padding-right: 16px; }
.progress, .help { color: var(--color-text_secondary); font-size: 0.8125em; }
.progress { margin-top: 24px; }
h2 { font-size: 2.5em; line-height: 1.15; font-weight: 300; margin: 12px 0 24px; }
p { margin: 0 0 20px; }
.text-link { text-decoration: underline; text-underline-offset: 4px; }
.consent { display: flex; align-items: flex-start; gap: 12px; margin-top: 28px; cursor: pointer; }
.consent input { width: 20px; height: 20px; flex-shrink: 0; margin-top: 3px; accent-color: var(--color-brand_main); }
fieldset { min-width: 0; padding: 0; margin: 0; border: 0; }
fieldset > label { display: block; margin-bottom: 8px; font-size: 0.875em; }
fieldset > input, textarea {
  display: block; box-sizing: border-box; width: 100%; min-height: 48px;
  margin: 0 0 20px; padding: 12px; border: 1px solid var(--color-bg_muted);
  border-radius: 4px; background: var(--color-bg_elevated); color: inherit; font: inherit;
}
textarea { resize: vertical; margin-bottom: 8px; }
.character-count { text-align: right; }
.actions { display: flex; justify-content: flex-end; gap: 16px; margin-top: 28px; }
.primary, .secondary { min-height: 48px; padding: 12px 24px; border-radius: 4px; font-size: 0.9375em; }
.primary { background: var(--color-brand_main); color: var(--color-darkest); font-weight: 500; }
.primary:hover:not(:disabled) { background: var(--color-brand_light); }
.secondary { border: 1px solid var(--color-bg_muted); }
button:disabled { opacity: 0.5; cursor: not-allowed; }
.error { color: #ffb4ab; margin-top: 20px; }
:is(button, a, input, textarea):focus-visible { outline: 2px solid var(--color-brand_light); outline-offset: 4px; }
h2:focus { outline: none; }
@media (max-width: 480px) {
  .contact-content { padding: 36px 20px 24px; }
  h2 { font-size: 2em; }
  .actions { gap: 12px; }
  .primary { flex: 1; }
}
</style>
